<script setup lang="ts">
/**
 * Список кандидатов на упоминание. Клавиатура здесь главнее мыши: набирают
 * `@` и продолжают печатать, не снимая рук.
 */
import { AppAvatar } from '@vinprop/design-system'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

export interface MentionUser {
  id: string
  name: string
  /** Ссылка на фото; пусто — в подсказке кружок с инициалами */
  avatar: string
}

const props = defineProps<{
  items: MentionUser[]
  command: (item: { id: string; label: string }) => void
}>()

const { t } = useI18n()

const selected = ref(0)

watch(
  () => props.items,
  () => {
    selected.value = 0
  },
)

const pick = (index: number) => {
  const item = props.items[index]

  if (item) props.command({ id: item.id, label: item.name })
}

/** Возвращает true, если клавиша обработана: иначе её получит документ. */
const onKeyDown = (event: KeyboardEvent): boolean => {
  if (!props.items.length) return false

  if (event.key === 'ArrowUp') {
    selected.value = (selected.value + props.items.length - 1) % props.items.length

    return true
  }

  if (event.key === 'ArrowDown') {
    selected.value = (selected.value + 1) % props.items.length

    return true
  }

  if (event.key === 'Enter' || event.key === 'Tab') {
    pick(selected.value)

    return true
  }

  return false
}

defineExpose({ onKeyDown })
</script>

<template>
  <div class="mention-list">
    <p v-if="!items.length" class="mention-list__empty">{{ t('editor.mention.empty') }}</p>

    <button
      v-for="(item, index) in items"
      :key="item.id"
      type="button"
      class="mention-list__item"
      :class="{ 'mention-list__item--active': index === selected }"
      @click="pick(index)"
    >
      <AppAvatar :name="item.name" :src="item.avatar" :size="20" />
      <span class="mention-list__name">{{ item.name }}</span>
    </button>
  </div>
</template>

<style scoped>
.mention-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 220px;
  max-height: 240px;
  padding: 4px;
  overflow-y: auto;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  box-shadow: var(--shadow-md);
}

.mention-list__empty {
  margin: 0;
  padding: 6px 8px;
  color: var(--color-text-tertiary);
  font-size: var(--font-size-sm);
}

.mention-list__item {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 8px;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  text-align: left;
  cursor: pointer;
}

.mention-list__item:hover,
.mention-list__item--active {
  background: var(--color-primary-tint);
  color: var(--color-primary-hover);
}
</style>
